// src/components/Admin/AdminWorkHours.jsx - Versione completa con autocompletamento e festività
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import { getDayInfo, getMonthHolidays } from '../../utils/holidaysUtils';
import { applyManualWorkHoursChanges, diffEditedEntries, snapshotEntries } from '../../services/workHoursAudit';
import './AdminWorkHours.css';

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return [currentYear, currentYear - 1, currentYear - 2].sort((a, b) => b - a);
};

const AdminWorkHours = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [timeEntries, setTimeEntries] = useState([]);
  // Valori caricati dal database (per salvare solo i giorni modificati) e ricarica dopo il salvataggio
  const [loadedSnapshot, setLoadedSnapshot] = useState({ key: '', entries: [] });
  const [reloadKey, setReloadKey] = useState(0);
  const [monthHolidays, setMonthHolidays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // Stato per l'autocompletamento
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);
 
  // Carica la lista degli utenti
  useEffect(() => {
    const fetchEmployees = async () => {
      console.log("Caricamento utenti...");
      setIsLoading(true);
      try {
        const usersCollection = collection(db, "users");
        const userSnapshot = await getDocs(usersCollection);
        const usersList = userSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .sort((a, b) => {
            if (a.role === 'admin' && b.role !== 'admin') return -1;
            if (a.role !== 'admin' && b.role === 'admin') return 1;
            
            const aName = a.nome && a.cognome ? `${a.nome} ${a.cognome}` : a.email;
            const bName = b.nome && b.cognome ? `${b.nome} ${b.cognome}` : b.email;
            return aName.localeCompare(bName);
          });
        
        console.log(`${usersList.length} utenti caricati`);
        setEmployees(usersList);
      } catch (error) {
        console.error("Errore nel caricamento degli utenti:", error);
        showNotification("Errore nel caricamento degli utenti", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  // Filtra i dipendenti in base al termine di ricerca
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredEmployees([]);
      setShowSuggestions(false);
      return;
    }

    const filtered = employees.filter(employee => {
      const fullName = employee.nome && employee.cognome 
        ? `${employee.nome} ${employee.cognome}`.toLowerCase()
        : employee.email.toLowerCase();
      
      return fullName.includes(searchTerm.toLowerCase());
    });

    setFilteredEmployees(filtered);
    setShowSuggestions(true);
  }, [searchTerm, employees]);

  // Carica le festività
  useEffect(() => {
    if (selectedMonth && selectedYear) {
      const monthInt = parseInt(selectedMonth);
      const yearInt = parseInt(selectedYear);
      const holidays = getMonthHolidays(monthInt, yearInt);
      setMonthHolidays(holidays);
      console.log(`Festività trovate per ${selectedMonth}/${selectedYear}:`, holidays);
    }
  }, [selectedMonth, selectedYear]);

  // Gestisce la selezione di un dipendente
  const handleEmployeeSelect = (employee) => {
    const displayName = employee.nome && employee.cognome 
      ? `${employee.nome} ${employee.cognome}`
      : employee.email;
    
    setSearchTerm(displayName);
    setSelectedEmployee(employee.id);
    setSelectedEmployeeData(employee);
    setShowSuggestions(false);
  };

  // Gestisce il cambio del termine di ricerca
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.trim() === '') {
      setSelectedEmployee('');
      setSelectedEmployeeData(null);
    }
  };

  // Gestisce il blur del campo di ricerca
  const handleSearchBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Genera tutti i giorni del mese con info festività
  const generateCompleteMonthDays = (month, year) => {
    const monthInt = parseInt(month);
    const yearInt = parseInt(year);
    const daysInMonth = new Date(yearInt, monthInt, 0).getDate();
    const days = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      // CORREZIONE: Crea la data string direttamente senza new Date()
      const dateString = `${yearInt}-${monthInt.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      
      // Usa getDayInfo con la stringa data corretta
      const dayInfo = getDayInfo(dateString);
      
      // Calcola il giorno della settimana dalla stringa data per evitare problemi di fuso orario
      const dateForDayName = new Date(yearInt, monthInt - 1, day);
      const dayOfWeekName = dateForDayName.toLocaleDateString('it-IT', { weekday: 'long' });
      
      days.push({
        date: dateString,
        day: day,
        dayOfWeek: dayOfWeekName,
        total: '',
        overtime: '',
        notes: '',
        isWeekend: dayInfo.isWeekend,
        isHoliday: dayInfo.isHoliday,
        holidayName: dayInfo.holidayName || null,
        hasData: false
      });
    }
    
    return days;
  };

  // Mostra le notifiche
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  // Carica le ore lavorative
  useEffect(() => {
    if (!selectedEmployee || !selectedMonth || !selectedYear) {
      setTimeEntries([]);
      return;
    }

    // Mostra i dati caricati e ne conserva una fotografia: al salvataggio
    // vengono scritti solo i giorni che differiscono da questa
    const loadedKey = `${selectedEmployee}_${selectedMonth}_${selectedYear}`;
    const showLoadedEntries = (entries) => {
      setTimeEntries(entries);
      setLoadedSnapshot({ key: loadedKey, entries: snapshotEntries(entries) });
    };

    const fetchTimeEntries = async () => {
      setIsLoading(true);
      try {
        const completeMonthData = generateCompleteMonthDays(selectedMonth, selectedYear);

        const docRef = doc(db, "workHours", `${selectedEmployee}_${selectedMonth}_${selectedYear}`);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log("Dati esistenti trovati:", data);
          
          if (data.entries && Array.isArray(data.entries)) {
            const existingDataMap = {};
            data.entries.forEach(entry => {
              if (entry.date) {
                existingDataMap[entry.date] = {
                  total: entry.total || '',
                  overtime: entry.overtime || 0,
                  notes: entry.notes || '',
                  hasData: true
                };
              }
            });
            
            const mergedData = completeMonthData.map(dayEntry => {
              const existingEntry = existingDataMap[dayEntry.date];
              if (existingEntry) {
                return {
                  ...dayEntry,
                  ...existingEntry
                };
              }
              return dayEntry;
            });
            
            showLoadedEntries(mergedData);
          } else {
            showLoadedEntries(completeMonthData);
          }
        } else {
          console.log("Documento non trovato, usando calendario completo vuoto");
          showLoadedEntries(completeMonthData);
        }
      } catch (error) {
        console.error("Errore nel caricamento delle ore lavorative:", error);
        showNotification("Errore nel caricamento delle ore lavorative", "error");
        
        const completeMonthData = generateCompleteMonthDays(selectedMonth, selectedYear);
        showLoadedEntries(completeMonthData);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTimeEntries();
  }, [selectedEmployee, selectedMonth, selectedYear, reloadKey]);

  // Gestisce il cambio di mese
  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  // Gestisce il cambio di anno
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // Gestisce l'input delle ore totali
  const handleTotalHoursChange = (index, value) => {
    let processedValue = value;
    
    if (value === "C" || value === "c") {
      processedValue = "C";
    } else if (value === "M" || value === "m") {
      processedValue = "M";
    } else if (value === "P" || value === "p") {
      processedValue = "P";
    } else if (value === "F" || value === "f") {
      processedValue = "F";
    } else if (value === "A" || value === "a") {
      processedValue = "A";
    }
    
    const newEntries = [...timeEntries];
    newEntries[index].total = processedValue;
    newEntries[index].hasData = true;
    
    if (["M", "P", "F", "A", "C"].includes(processedValue)) {
      newEntries[index].overtime = '';
    }
    
    setTimeEntries(newEntries);
  };

  // Gestisce l'input delle ore di straordinario
  const handleOvertimeChange = (index, value) => {
    const newEntries = [...timeEntries];
    newEntries[index].overtime = value;
    newEntries[index].hasData = true;
    setTimeEntries(newEntries);
  };

  // Gestisce l'input delle note
  const handleNotesChange = (index, value) => {
    const newEntries = [...timeEntries];
    newEntries[index].notes = value;
    newEntries[index].hasData = true;
    setTimeEntries(newEntries);
  };

  // Salva le ore lavorative
  const handleSaveWorkHours = async () => {
    if (!selectedEmployeeData) {
      showNotification("Seleziona un dipendente valido", "error");
      return;
    }

    // I dati a video devono essere quelli del dipendente/mese selezionato
    if (isLoading || loadedSnapshot.key !== `${selectedEmployee}_${selectedMonth}_${selectedYear}`) {
      showNotification("Attendi il caricamento delle ore prima di salvare", "error");
      return;
    }

    setIsSaving(true);
    try {
      // Si salvano solo i giorni modificati a video, in transazione sopra lo
      // stato attuale del database: niente riscrittura del mese intero e niente
      // giorni vuoti. Ogni modifica viene annotata con autore, data e valori
      // prima → dopo (vedi services/workHoursAudit.js).
      const changes = diffEditedEntries(loadedSnapshot.entries, timeEntries).map(entry => ({
        date: entry.date,
        day: entry.day,
        total: entry.total,
        overtime: entry.overtime,
        notes: entry.notes,
        isWeekend: entry.isWeekend
      }));

      if (changes.length === 0) {
        showNotification("Nessuna modifica da salvare", "success");
        return;
      }

      const result = await applyManualWorkHoursChanges({
        userId: selectedEmployee,
        month: selectedMonth,
        year: selectedYear,
        changes,
        source: 'pannello-ore-lavorative'
      });

      // Ricarica dal database per mostrare le note di modifica appena scritte
      setReloadKey(key => key + 1);

      showNotification(`Ore lavorative salvate: ${result.changedDays} ${result.changedDays === 1 ? 'giorno modificato' : 'giorni modificati'}`, "success");
      console.log("✅ Salvataggio completato con successo", result.applied);

    } catch (error) {
      console.error("❌ Errore dettagliato nel salvataggio:", error);
      
      // Mostra un messaggio di errore più specifico
      let errorMessage = "Errore nel salvataggio delle ore lavorative";
      if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      showNotification(errorMessage, "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="admin-work-hours">
      <h2>Gestione Ore Lavorative</h2>
      
      {/* Notifiche */}
      {notification.show && (
        <div className={`alert ${notification.type === 'error' ? 'alert-danger' : 'alert-success'}`}>
          {notification.message}
        </div>
      )}
 
      {isLoading && employees.length === 0 ? (
        <div className="loading">Caricamento dipendenti...</div>
      ) : (
        <>
          <div className="filters-container">
            {/* Campo di ricerca con autocompletamento */}
            <div className="form-group" style={{ position: 'relative' }}>
              <label htmlFor="employee-search">Cerca Dipendente:</label>
              <input
                id="employee-search"
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => searchTerm && setShowSuggestions(true)}
                onBlur={handleSearchBlur}
                placeholder="Digita nome e cognome..."
                className="form-control"
                style={{ width: '100%', maxWidth: '300px' }}
              />
              
              {/* Lista suggerimenti */}
              {showSuggestions && filteredEmployees.length > 0 && (
                <div className="suggestions-dropdown" style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderTop: 'none',
                  borderRadius: '0 0 4px 4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {filteredEmployees.map(employee => (
                    <div
                      key={employee.id}
                      className="suggestion-item"
                      onClick={() => handleEmployeeSelect(employee)}
                      style={{
                        padding: '10px 15px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <div style={{ fontWeight: 'bold' }}>
                        {employee.nome && employee.cognome 
                          ? `${employee.nome} ${employee.cognome}`
                          : employee.email}
                      </div>
                      {employee.role === 'admin' && (
                        <small style={{ color: '#666' }}>Admin</small>
                      )}
                      <small style={{ color: '#999', display: 'block' }}>
                        {employee.email}
                      </small>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="month-select">Mese:</label>
              <select
                id="month-select"
                value={selectedMonth}
                onChange={handleMonthChange}
                className="form-control"
              >
                <option value="">Seleziona mese</option>
                <option value="1">Gennaio</option>
                <option value="2">Febbraio</option>
                <option value="3">Marzo</option>
                <option value="4">Aprile</option>
                <option value="5">Maggio</option>
                <option value="6">Giugno</option>
                <option value="7">Luglio</option>
                <option value="8">Agosto</option>
                <option value="9">Settembre</option>
                <option value="10">Ottobre</option>
                <option value="11">Novembre</option>
                <option value="12">Dicembre</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="year-select">Anno:</label>
              <select
                id="year-select"
                value={selectedYear}
                onChange={handleYearChange}
                className="form-control"
              >
                {generateYearOptions().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {selectedEmployee && selectedMonth && selectedYear ? (
            <div className="timesheet-container">
              <h3>
                Ore Lavorative - {selectedEmployeeData?.nome && selectedEmployeeData?.cognome 
                  ? `${selectedEmployeeData.nome} ${selectedEmployeeData.cognome}`
                  : selectedEmployeeData?.email} 
                - {selectedMonth}/{selectedYear}
              </h3>
              
              <div className="table-responsive">
                <table className="work-hours-table" id="timesheet-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Giorno</th>
                      <th>Ore Totali</th>
                      <th>Straordinario</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="5" className="loading">Caricamento...</td>
                      </tr>
                    ) : (
                      timeEntries.map((entry, index) => (
                        <tr 
                          key={entry.date} 
                          className={`
                            ${entry.isWeekend ? 'weekend-row' : ''} 
                            ${entry.isHoliday ? 'holiday-row' : ''}
                            ${!entry.hasData ? 'no-data-row' : ''}
                          `.trim()}
                          title={entry.holidayName || ''}
                        >
                          <td>
                            {entry.day}/{selectedMonth}
                            {entry.isHoliday && (
                              <span className="holiday-indicator" title={entry.holidayName}>
                                🎉
                              </span>
                            )}
                          </td>
                          <td>
                            {entry.dayOfWeek}
                            {entry.isWeekend && <span className="badge badge-weekend">Weekend</span>}
                            {entry.isHoliday && <span className="badge badge-holiday">{entry.holidayName}</span>}
                          </td>
                          <td>
                          <input 
  type="text" 
  value={entry.total} 
  onChange={e => handleTotalHoursChange(index, e.target.value)}
  className="form-control"
  style={{
    fontWeight: entry.hasData ? 'bold' : 'normal',
    width: '100px'
  }}
  placeholder={entry.isWeekend || entry.isHoliday ? "0-8 o M/P/F/A/C (modificabile)" : "0-8 o M/P/F/A/C"}
  title="Inserisci un valore tra 0 e 8 o una lettera speciale (M=Malattia, P=Permesso, F=Ferie, A=Assenza, C=Cassa Integrazione). Ora modificabile anche per weekend e festività."
/>
                          </td>
                          <td>
                            <input 
                              type="number" 
                              value={entry.overtime || ""} 
                              onChange={e => handleOvertimeChange(index, e.target.value)}
                              min="0"
                              max="12"
                              className="form-control"
                              style={{ width: '80px' }}
                              disabled={["M", "P", "F", "A", "C"].includes(entry.total)}
                              placeholder="0-12"
                              title="Inserisci le ore di straordinario (max 12, disponibile solo per giorni lavorati)"
                            />
                          </td>
                          <td>
                            <input 
                              type="text" 
                              value={entry.notes || ''} 
                              onChange={e => handleNotesChange(index, e.target.value)}
                              placeholder="Note opzionali..."
                              className="form-control"
                              style={{ minWidth: '150px' }}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="action-buttons">
                <button 
                  className="btn btn-primary" 
                  onClick={handleSaveWorkHours}
                  disabled={isSaving || isLoading}
                >
                  {isSaving ? 'Salvataggio in corso...' : 'Salva Ore Lavorative'}
                </button>
              </div>
            </div>
          ) : (
            <div className="select-message">
              Seleziona un dipendente e un mese per gestire le ore lavorative
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminWorkHours;